import { ThemeMode } from '@sternum/web/components/theme/main/ThemeMode.context';
import { TimeRange } from '@sternum/web/components/TimeRangeDropdown/TimeRange.types';
import { SCSWorkloadsTab } from '@sternum/web/features/workloads/models/workloads.models';

import { LocalStorage } from './localStorage';

export type AppStorageSchema = {
  token?: string;
  themeMode?: ThemeMode;
  lastLocationPath?: string;
  isLogsFiltersPanelOpen: boolean;
  isLogsChartVisible?: boolean;
  isLogsTablePolling?: boolean;
  appMenuIsAutoExpanded?: boolean;
  appMenuIsFullMenuVisible?: boolean;
  appMenuIsDashboardMenuVisible?: boolean;
  isAnomaliesGraphVisible?: boolean;
  isAlertsFiltersSidebarOpen?: boolean;
  isAuditLogFiltersSidebarOpen?: boolean;
  isDevicesAlertsFiltersSidebarOpen?: boolean;
  isFleetsViewAlertsFiltersSidebarOpen?: boolean;
  isAnomaliesFiltersSidebarOpen?: boolean;
  isAlertsPolling?: boolean;
  isAlertRulesFiltersSidebarOpen?: boolean;
  lastLogsQueryFilterValue?: string;
  lastFleetsViewLogsQueryFilterValue?: string;
  sharedTimeRange?: string;

  areAlertNotesVisible?: boolean;
  areAnomalyNotesVisible?: boolean;

  isCookiePolicyAccepted?: boolean;

  workloads_isAlertsFiltersSidebarOpen?: boolean;
  workloads_isAlertsPolling?: boolean;
  workloads_areAlertNotesVisible?: boolean;
  workloads_lastTabOpened?: SCSWorkloadsTab;

  device_isDeviceFiltersSidebarOpen?: boolean;
  device_isSidebarFilterOpenForTabAnomalies?: boolean;
  device_isSidebarFilterOpenForTabLogs?: boolean;

  deviceDetails_isSidebarFilterOpenForTabAlerts?: boolean;
  deviceDetails_isSidebarFilterOpenForTabInventory?: boolean;
  deviceDetails_isSidebarFilterOpenForTabAttacks?: boolean;
  deviceDetails_isSidebarFilterOpenForTabAnomalies?: boolean;
  deviceDetails_isSidebarFilterOpenForTabLogs?: boolean;
  deviceDetails_lastLogsQueryFilterValue?: string;

  fleetsView_isSidebarFilterOpenForTabAlerts?: boolean;
  fleetsView_isSidebarFilterOpenForTabInventory?: boolean;
  fleetsView_isSidebarFilterOpenForTabAttacks?: boolean;
  fleetsView_isSidebarFilterOpenForTabAnomalies?: boolean;
  fleetsView_isSidebarFilterOpenForTabLogs?: boolean;
  fleetsView_productId?: string;
  fleetsView_lastLogsQueryFilterValue?: string;

  attack_isPolling?: boolean;
  attack_isFiltersSidebarOpen?: boolean;
  attack_areNotesVisible?: boolean;
};

class AppStorage extends LocalStorage<AppStorageSchema> {
  public workloads;
  public device;
  public deviceDetails;
  public fleetsView;
  public attack;

  constructor() {
    super();

    // eslint-disable-next-line @typescript-eslint/no-this-alias
    const self = this;

    this.workloads = {
      getIsAlertsFiltersSidebarOpen(): boolean | undefined {
        return self.get('workloads_isAlertsFiltersSidebarOpen');
      },

      setIsAlertsFiltersSidebarOpen(isAlertsFiltersSidebarOpen: boolean): void {
        self.set('workloads_isAlertsFiltersSidebarOpen', isAlertsFiltersSidebarOpen);
      },

      getIsAlertsPolling(): boolean | undefined {
        return self.get('workloads_isAlertsPolling');
      },

      setIsAlertsPolling(isAlertsPolling: boolean): void {
        self.set('workloads_isAlertsPolling', isAlertsPolling);
      },

      getAreAlertNotesVisible(): boolean | undefined {
        return self.get('workloads_areAlertNotesVisible');
      },

      setAreAlertNotesVisible(areWorkloadAlertNotesVisible: boolean): void {
        self.set('workloads_areAlertNotesVisible', areWorkloadAlertNotesVisible);
      },

      getLastTabOpened(): SCSWorkloadsTab | undefined {
        return self.get('workloads_lastTabOpened');
      },

      setLastTabOpened(tab: SCSWorkloadsTab): void {
        self.set('workloads_lastTabOpened', tab);
      },

      removeLastTabOpened(): void {
        self.remove('workloads_lastTabOpened');
      },
    };

    this.device = {
      getIsDeviceFiltersSidebarOpen(): boolean | undefined {
        return self.get('device_isDeviceFiltersSidebarOpen');
      },

      setIsDeviceFiltersSidebarOpen(isDeviceFiltersSidebarOpen: boolean): void {
        self.set('device_isDeviceFiltersSidebarOpen', isDeviceFiltersSidebarOpen);
      },

      getIsSidebarFilterOpenForTabAnomalies(): boolean | undefined {
        return self.get('device_isSidebarFilterOpenForTabAnomalies');
      },

      getIsSidebarFilterOpenForTabLogs(): boolean | undefined {
        return self.get('device_isSidebarFilterOpenForTabLogs');
      },

      setIsSidebarFilterOpenForTabLogs(isSidebarFilterOpenForTabLogs: boolean): void {
        self.set('device_isSidebarFilterOpenForTabLogs', isSidebarFilterOpenForTabLogs);
      },
    };

    this.deviceDetails = {
      getIsSidebarFilterOpenForTabAlerts(): boolean | undefined {
        return self.get('deviceDetails_isSidebarFilterOpenForTabAlerts');
      },

      setIsSidebarFilterOpenForTabAlerts(isSidebarFilterOpenForTabAlerts: boolean): void {
        self.set('deviceDetails_isSidebarFilterOpenForTabAlerts', isSidebarFilterOpenForTabAlerts);
      },

      getIsSidebarFilterOpenForTabInventory(): boolean | undefined {
        return self.get('deviceDetails_isSidebarFilterOpenForTabInventory');
      },

      setIsSidebarFilterOpenForTabInventory(isSidebarFilterOpenForTabInventory: boolean): void {
        self.set('deviceDetails_isSidebarFilterOpenForTabInventory', isSidebarFilterOpenForTabInventory);
      },

      getIsSidebarFilterOpenForTabAttacks(): boolean | undefined {
        return self.get('deviceDetails_isSidebarFilterOpenForTabAttacks');
      },

      getIsSidebarFilterOpenForTabAnomalies(): boolean | undefined {
        return self.get('deviceDetails_isSidebarFilterOpenForTabAnomalies');
      },

      setIsSidebarFilterOpenForTabAttacks(isSidebarFilterOpenForTabAttacks: boolean): void {
        self.set('deviceDetails_isSidebarFilterOpenForTabAttacks', isSidebarFilterOpenForTabAttacks);
      },

      setIsSidebarFilterOpenForTabAnomalies(isSidebarFilterOpenForTabAnomalies: boolean): void {
        self.set('deviceDetails_isSidebarFilterOpenForTabAnomalies', isSidebarFilterOpenForTabAnomalies);
      },

      getIsSidebarFilterOpenForTabLogs(): boolean | undefined {
        return self.get('deviceDetails_isSidebarFilterOpenForTabLogs');
      },

      setIsSidebarFilterOpenForTabLogs(isSidebarFilterOpenForTabLogs: boolean): void {
        self.set('deviceDetails_isSidebarFilterOpenForTabLogs', isSidebarFilterOpenForTabLogs);
      },

      getLastLogsQueryFilterValue(): string | undefined {
        return self.get('deviceDetails_lastLogsQueryFilterValue');
      },

      setLastLogsQueryFilterValue(lastLogsQueryFilterValue: string): void {
        self.set('deviceDetails_lastLogsQueryFilterValue', lastLogsQueryFilterValue);
      },
    };

    this.fleetsView = {
      getIsSidebarFilterOpenForTabAlerts(): boolean | undefined {
        return self.get('fleetsView_isSidebarFilterOpenForTabAlerts');
      },

      setIsSidebarFilterOpenForTabAlerts(isSidebarFilterOpenForTabAlerts: boolean): void {
        self.set('fleetsView_isSidebarFilterOpenForTabAlerts', isSidebarFilterOpenForTabAlerts);
      },

      getIsSidebarFilterOpenForTabInventory(): boolean | undefined {
        return self.get('fleetsView_isSidebarFilterOpenForTabInventory');
      },

      setIsSidebarFilterOpenForTabInventory(isSidebarFilterOpenForTabInventory: boolean): void {
        self.set('fleetsView_isSidebarFilterOpenForTabInventory', isSidebarFilterOpenForTabInventory);
      },

      getIsSidebarFilterOpenForTabAttacks(): boolean | undefined {
        return self.get('fleetsView_isSidebarFilterOpenForTabAttacks');
      },

      getIsSidebarFilterOpenForTabAnomalies(): boolean | undefined {
        return self.get('fleetsView_isSidebarFilterOpenForTabAnomalies');
      },

      setIsSidebarFilterOpenForTabAttacks(isSidebarFilterOpenForTabAttacks: boolean): void {
        self.set('fleetsView_isSidebarFilterOpenForTabAttacks', isSidebarFilterOpenForTabAttacks);
      },

      setIsSidebarFilterOpenForTabAnomalies(isSidebarFilterOpenForTabAnomalies: boolean): void {
        self.set('fleetsView_isSidebarFilterOpenForTabAnomalies', isSidebarFilterOpenForTabAnomalies);
      },

      getIsSidebarFilterOpenForTabLogs(): boolean | undefined {
        return self.get('fleetsView_isSidebarFilterOpenForTabLogs');
      },

      setIsSidebarFilterOpenForTabLogs(isSidebarFilterOpenForTabLogs: boolean): void {
        self.set('fleetsView_isSidebarFilterOpenForTabLogs', isSidebarFilterOpenForTabLogs);
      },

      getProductId(): string | undefined {
        return self.get('fleetsView_productId');
      },

      setProductId(newProductId: string): void {
        self.set('fleetsView_productId', newProductId);
      },

      getLastLogsQueryFilterValue(): string | undefined {
        return self.get('fleetsView_lastLogsQueryFilterValue');
      },

      setLastLogsQueryFilterValue(lastLogsQueryFilterValue: string): void {
        self.set('fleetsView_lastLogsQueryFilterValue', lastLogsQueryFilterValue);
      },
    };

    this.attack = {
      getIsPolling(): boolean | undefined {
        return self.get('attack_isPolling');
      },

      setIsPolling(isAttacksPolling: boolean): void {
        self.set('attack_isPolling', isAttacksPolling);
      },

      getIsFiltersSidebarOpen(): boolean | undefined {
        return self.get('attack_isFiltersSidebarOpen');
      },

      setIsFiltersSidebarOpen(isAttacksFiltersSidebarOpen: boolean): void {
        self.set('attack_isFiltersSidebarOpen', isAttacksFiltersSidebarOpen);
      },

      getAreNotesVisible(): boolean | undefined {
        return self.get('attack_areNotesVisible');
      },

      setAreNotesVisible(areAttackNotesVisible: boolean): void {
        self.set('attack_areNotesVisible', areAttackNotesVisible);
      },
    };
  }

  clearClientSpecificData(): void {
    this.remove('lastLogsQueryFilterValue');
    this.remove('lastFleetsViewLogsQueryFilterValue');
    this.remove('workloads_lastTabOpened');
    this.remove('deviceDetails_lastLogsQueryFilterValue');
    this.remove('fleetsView_productId');
    this.remove('fleetsView_lastLogsQueryFilterValue');
  }

  getToken(): string {
    return this.get('token') || '';
  }

  setToken(apiKey: string): void {
    return this.set('token', apiKey);
  }

  removeToken(): void {
    this.remove('token');
  }

  getThemeMode(): ThemeMode | undefined {
    return this.get('themeMode');
  }

  setThemeMode(mode: ThemeMode): void {
    this.set('themeMode', mode);
  }

  getLastLocationPath(): string | undefined {
    return this.get('lastLocationPath');
  }

  setLastLocationPath(path: string): void {
    this.set('lastLocationPath', path);
  }

  removeLastLocationPath(): void {
    this.remove('lastLocationPath');
  }

  getIsLogsFiltersPanelOpen(): boolean | undefined {
    return this.get('isLogsFiltersPanelOpen');
  }

  setIsLogsFiltersPanelOpen(isPanelOpen: boolean): void {
    this.set('isLogsFiltersPanelOpen', isPanelOpen);
  }

  getIsAnomaliesGraphVisible(): boolean | undefined {
    return this.get('isAnomaliesGraphVisible');
  }

  setIsAnomaliesGraphVisible(isAnomaliesGraphVisible: boolean): void {
    this.set('isAnomaliesGraphVisible', isAnomaliesGraphVisible);
  }

  getIsAnomaliesFiltersSidebarOpen(): boolean | undefined {
    return this.get('isAnomaliesFiltersSidebarOpen');
  }

  setIsAnomaliesFiltersSidebarOpen(isAnomaliesFiltersSidebarOpen: boolean): void {
    this.set('isAnomaliesFiltersSidebarOpen', isAnomaliesFiltersSidebarOpen);
  }

  getIsLogsChartVisible(): boolean | undefined {
    return this.get('isLogsChartVisible');
  }

  setIsLogsChartVisible(isChartVisible: boolean): void {
    this.set('isLogsChartVisible', isChartVisible);
  }

  getIsLogsTablePolling(): boolean | undefined {
    return this.get('isLogsTablePolling');
  }

  setIsLogsTablePolling(isPolling: boolean): void {
    this.set('isLogsTablePolling', isPolling);
  }

  // App Container With Menu
  getAppMenuIsAutoExpanded(): boolean | undefined {
    return this.get('appMenuIsAutoExpanded');
  }

  setAppMenuIsAutoExpanded(isAutoExpanded: boolean): void {
    this.set('appMenuIsAutoExpanded', isAutoExpanded);
  }

  getAppMenuIsFullMenuVisible(): boolean | undefined {
    return this.get('appMenuIsFullMenuVisible');
  }

  setAppMenuIsFullMenuVisible(isFullMenuVisible: boolean): void {
    this.set('appMenuIsFullMenuVisible', isFullMenuVisible);
  }

  getAppMenuIsDashboardMenuVisible(): boolean | undefined {
    return this.get('appMenuIsDashboardMenuVisible');
  }

  setAppMenuIsDashboardMenuVisible(isDashboardMenuVisible: boolean): void {
    this.set('appMenuIsDashboardMenuVisible', isDashboardMenuVisible);
  }

  getIsAlertsFiltersSidebarOpen(): boolean | undefined {
    return this.get('isAlertsFiltersSidebarOpen');
  }

  setIsAlertsFiltersSidebarOpen(isAlertsFiltersSidebarOpen: boolean): void {
    this.set('isAlertsFiltersSidebarOpen', isAlertsFiltersSidebarOpen);
  }

  getIsDevicesAlertsFiltersSidebarOpen(): boolean | undefined {
    return this.get('isDevicesAlertsFiltersSidebarOpen');
  }

  setIsDevicesAlertsFiltersSidebarOpen(isDevicesAlertsFiltersSidebarOpen: boolean): void {
    this.set('isDevicesAlertsFiltersSidebarOpen', isDevicesAlertsFiltersSidebarOpen);
  }

  getIsFleetsViewAlertsFiltersSidebarOpen(): boolean | undefined {
    return this.get('isFleetsViewAlertsFiltersSidebarOpen');
  }

  setIsFleetsViewAlertsFiltersSidebarOpen(isFleetsViewAlertsFiltersSidebarOpen: boolean): void {
    this.set('isFleetsViewAlertsFiltersSidebarOpen', isFleetsViewAlertsFiltersSidebarOpen);
  }

  getIsAlertsPolling(): boolean | undefined {
    return this.get('isAlertsPolling');
  }

  setIsAlertsPolling(isAlertsPolling: boolean): void {
    this.set('isAlertsPolling', isAlertsPolling);
  }

  getIsAlertRulesFiltersSidebarOpen(): boolean | undefined {
    return this.get('isAlertRulesFiltersSidebarOpen');
  }

  setIsAlertRulesFiltersSidebarOpen(isAlertRulesFiltersSidebarOpen: boolean): void {
    this.set('isAlertRulesFiltersSidebarOpen', isAlertRulesFiltersSidebarOpen);
  }

  getIsAuditLogFiltersSidebarOpen(): boolean | undefined {
    return this.get('isAlertRulesFiltersSidebarOpen');
  }

  setIsAuditLogFiltersSidebarOpen(isAuditLogFiltersSidebarOpen: boolean): void {
    this.set('isAuditLogFiltersSidebarOpen', isAuditLogFiltersSidebarOpen);
  }

  getAreAlertNotesVisible(): boolean | undefined {
    return this.get('areAlertNotesVisible');
  }

  setAreAlertNotesVisible(areAlertNotesVisible: boolean): void {
    this.set('areAlertNotesVisible', areAlertNotesVisible);
  }

  getAreAnomalyNotesVisible(): boolean | undefined {
    return this.get('areAnomalyNotesVisible');
  }

  setAreAnomalyNotesVisible(areAnomalyNotesVisible: boolean): void {
    this.set('areAnomalyNotesVisible', areAnomalyNotesVisible);
  }

  getLastLogsQueryFilterValue(): string | undefined {
    return this.get('lastLogsQueryFilterValue');
  }

  setLastLogsQueryFilterValue(lastLogsQueryFilterValue: string): void {
    this.set('lastLogsQueryFilterValue', lastLogsQueryFilterValue);
  }

  setIsCookiePolicyAccepted(isCookiePolicyAccepted: boolean): void {
    this.set('isCookiePolicyAccepted', isCookiePolicyAccepted);
  }

  getIsCookiePolicyAccepted(): boolean | undefined {
    return this.get('isCookiePolicyAccepted');
  }

  setSharedTimeRange(timeRange: TimeRange): void {
    this.set('sharedTimeRange', JSON.stringify(timeRange));
  }

  getSharedTimeRange(): TimeRange | undefined {
    const timeRange = this.get('sharedTimeRange');

    if (timeRange) {
      return JSON.parse(timeRange);
    }

    return undefined;
  }
}

export const appStorage = new AppStorage();
